/**
 * The Add to mailshot box's tick-box for homes BROUGHT IN FROM A QUOTE that HubSpot no longer has (EST-230, Oct 2026):
 * its words, worked out apart from the box so that they can be tested.
 *
 * The rule is models/quoteGone.js (the matching's stamp, which counts only while it names the quote the home is
 * linked to now). This is what the box SAYS of it, and which homes it leaves out:
 *  - only a home brought in FROM A QUOTE (the "hubspot" kind: models/propertySource.js). A scraped home whose matched
 *    quote is gone is a home like any other here: the quote was never why it is mailed;
 *  - left out unless unticked; unticked, the consequence in words;
 *  - ⚠️ the stamp is the matching's, made nightly: a quote HubSpot stopped giving since the last full matching is not
 *    known here yet, and the box says so.
 *
 * ⚠️ ONE SET, as with the development flag: the number on the tick-box, the "Show them" list and the figure in the
 * message after the add are all `goneQuoteHomesAmong` of the same homes.
 */
import { quoteGoneFromHubspot } from '../models/quoteGone';
import { sourceOf, HUBSPOT } from '../models/propertySource';
import { onePerHome, flaggedRow } from './developmentChoice';

const n = (v) => Number(v || 0).toLocaleString('en-GB');
const homes = (count) => (Number(count) === 1 ? 'home' : 'homes');
const list = (v) => (Array.isArray(v) ? v : []);

const isGoneQuoteHome = (p) => sourceOf(p).kind === HUBSPOT && quoteGoneFromHubspot(p);

/** The homes of a selection brought in from a quote HubSpot no longer has, each once, in the order given. */
export const goneQuoteHomesAmong = (properties) => onePerHome(properties).filter(isGoneQuoteHome);

/** A selection without them. */
export const withoutGoneQuoteHomes = (properties) => list(properties).filter((p) => !isGoneQuoteHome(p));

/**
 * The tick-box and its lines.
 * @param {Object[]} properties the homes that would otherwise be added (after the status, mailing-rule and price choices)
 * @param {boolean}  leaveOut   the tick-box
 */
export function goneQuoteChoice(properties, leaveOut = true) {
  const gone = goneQuoteHomesAmong(properties).length;
  return {
    gone,
    label: `Leave out ${n(gone)} ${homes(gone)} brought in from a quote HubSpot no longer has`,
    hint: '(On by default: the quote was deleted or merged away in HubSpot. As the nightly matching last found them.)',
    untickedWords: !leaveOut && gone ? `${n(gone)} ${homes(gone)} brought in from a quote HubSpot no longer has WILL be added.` : null,
  };
}

/** What the message after an add says of it. Empty when there is nothing to say. */
export function goneQuoteNote({ hadRecords, leaveOut, gone, goneSent }) {
  // (said before the development flag's own line, which says why: the homes were given by id only)
  if (!hadRecords) return 'NOT read for quotes HubSpot no longer has';
  if (!gone) return '';
  if (leaveOut) return `${n(gone)} brought in from a quote HubSpot no longer has left out`;
  const sent = Number(goneSent || 0);
  return `the box for homes brought in from a quote HubSpot no longer has was UNTICKED: ${n(sent)} of the ${n(gone)} such ${homes(gone)} ${sent === 1 ? 'was' : 'were'} among those sent to be added`;
}

/** One such home for the "Show them" list: its address in a line, and where its own page is. */
export function goneQuoteRow(property) {
  const { id, address, href } = flaggedRow(property);
  return { id, address, href };
}
